/** Corps d'erreur normalise renvoye par l'API (RFC 7807 enrichi). */
export interface ProblemeApi {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  correlationId?: string;
  errors?: Record<string, string[]>;
}

export type NatureErreur =
  | 'validation'
  | 'introuvable'
  | 'conflit'
  | 'interdit'
  | 'non-authentifie'
  | 'reseau'
  | 'serveur';

/**
 * Erreur d'API normalisee.
 *
 * `interdit` est traite a part du reste dans cette application : un 403 sur une route
 * d'administration n'est pas un incident technique, c'est le refus par defaut qui
 * fonctionne (§3). L'interface doit le dire clairement au lieu d'afficher une erreur
 * generique qui laisserait croire a une panne.
 */
export class ErreurApi extends Error {
  readonly nature: NatureErreur;
  readonly code: string;
  readonly statut: number;
  readonly correlationId: string | undefined;
  readonly champs: Record<string, string[]>;

  constructor(options: {
    message: string;
    nature: NatureErreur;
    code?: string;
    statut?: number;
    correlationId?: string;
    champs?: Record<string, string[]>;
  }) {
    super(options.message);
    this.name = 'ErreurApi';
    this.nature = options.nature;
    this.code = options.code ?? 'inconnu';
    this.statut = options.statut ?? 0;
    this.correlationId = options.correlationId;
    this.champs = options.champs ?? {};
  }

  /** Vrai si rejouer la requete a une chance d'aboutir. */
  get estTransitoire(): boolean {
    return this.nature === 'reseau' || this.nature === 'serveur';
  }

  /** Vrai si l'API a refuse l'acces : le compte n'est pas administrateur plateforme. */
  get estRefusAcces(): boolean {
    return this.nature === 'interdit' || this.nature === 'non-authentifie';
  }
}

export function natureDepuisStatut(statut: number): NatureErreur {
  if (statut === 400 || statut === 422) return 'validation';
  if (statut === 401) return 'non-authentifie';
  if (statut === 403) return 'interdit';
  if (statut === 404) return 'introuvable';
  if (statut === 409) return 'conflit';
  if (statut >= 500) return 'serveur';
  return 'serveur';
}

export function depuisReponse(statut: number, corps: unknown): ErreurApi {
  const probleme = (corps ?? {}) as ProblemeApi;

  return new ErreurApi({
    message: probleme.detail ?? probleme.title ?? `Erreur ${statut}.`,
    nature: natureDepuisStatut(statut),
    code: probleme.code ?? 'inconnu',
    statut,
    correlationId: probleme.correlationId,
    champs: probleme.errors ?? {},
  });
}

export function erreurReseau(cause: unknown): ErreurApi {
  return new ErreurApi({
    message: "L'API est injoignable.",
    nature: 'reseau',
    code: 'reseau',
    statut: 0,
    ...(cause instanceof Error ? { correlationId: undefined } : {}),
  });
}

export function messageLisible(erreur: unknown): string {
  if (erreur instanceof ErreurApi) return erreur.message;
  if (erreur instanceof Error) return erreur.message;
  return 'Une erreur inattendue est survenue.';
}
